import { useState } from 'react';
import { useConfirmationModal, useSnackbars } from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import type { Tag } from '@customerio/hydra-ui-api';
import { useAnalytics } from '@/services/analytics';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import {
  useDeleteDataIndexAttribute,
  useUpdateDataIndexAttributeMetadata,
  useUpdateDataIndexAttributeTags,
  useUpdateDataIndexEventMetadata,
  useUpdateDataIndexEventTags,
} from '@/services/db/data-index/mutations';
import type { AttributeScope } from '@/services/db/data-index/entity';
import { dataIndexListPath } from '../urls';
import { getLabelForItem } from '../utils/item';
import {
  DataIndexLocation,
  NOT_SENSITIVE_PRIVACY_LEVEL,
  SENSITIVE_PRIVACY_LEVEL,
  type DataIndexItemKind,
} from '../utils/types';
import { DeleteAttributeMessage } from './delete-attribute-message';

/**
 * The writes an item's own page can make: its description and tags, whether
 * it is redacted, and whether it exists at all.
 *
 * Kept apart from the menu so the menu is only a menu — each of these has a
 * different failure to report, and deleting has a confirmation to get
 * through first.
 */

/** The privacy level the backend uses for an attribute someone marked sensitive. */

export type ItemWriteTarget = {
  workspaceId: number;
  kind: DataIndexItemKind;
  scope: AttributeScope;
  name: string;
  /** The description the page shows, which is what a save is compared against. */
  description: string;
  tags: readonly Tag[];
  privacyLevel: number | null;
};

export type ItemWrites = {
  /** Saves the description and tags together; resolves to whether that worked. */
  saveDetails: (values: { description: string; tagIds: number[] }) => Promise<boolean>;
  toggleSensitivity: () => Promise<void>;
  isChangingSensitivity: boolean;
  /** Asks first, then deletes and leaves for the list. */
  confirmDelete: () => Promise<void>;
};

export function useItemWrites(target: ItemWriteTarget): ItemWrites {
  const { workspaceId, kind, scope, name, description, tags, privacyLevel } = target;
  const analytics = useAnalytics();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const router = useRouter();
  const navigate = useSafeNavigate();
  const taxonomy = useProductTaxonomy();
  const [isChangingSensitivity, setIsChangingSensitivity] = useState(false);

  const analyticsType = getLabelForItem(kind);
  const writeMetadata = useUpdateDataIndexAttributeMetadata({ workspaceId });
  const writeTags = useUpdateDataIndexAttributeTags({ workspaceId });
  const writeEventMetadata = useUpdateDataIndexEventMetadata({ workspaceId });
  const writeEventTags = useUpdateDataIndexEventTags({ workspaceId });
  const remove = useDeleteDataIndexAttribute({ workspaceId });

  const saveDescription = async (next: string) => {
    await (kind === 'event'
      ? writeEventMetadata.mutateAsync({ name, description: next })
      : writeMetadata.mutateAsync({ scope, name, description: next }));
    void analytics.track('dataIndex', 'descriptionUpdated', {
      type: analyticsType,
      location: DataIndexLocation.DetailsPage,
    });
  };

  const saveTags = async (tagIds: number[]) => {
    await (kind === 'event'
      ? writeEventTags.mutateAsync({ name, tagIds })
      : writeTags.mutateAsync({ scope, name, tagIds }));
    void analytics.track('dataIndex', 'tagsUpdated', {
      type: analyticsType,
      location: DataIndexLocation.DetailsPage,
      tag_count: tagIds.length,
    });
  };

  const saveDetails = async (values: { description: string; tagIds: number[] }) => {
    const current = tags.map((tag) => tag.id).sort((a, b) => a - b);
    const next = [...values.tagIds].sort((a, b) => a - b);

    try {
      if (values.description !== description) {
        await saveDescription(values.description);
      }
      if (current.join() !== next.join()) {
        await saveTags(next);
      }
      return true;
    } catch {
      return false;
    }
  };

  const toggleSensitivity = async () => {
    setIsChangingSensitivity(true);
    try {
      await writeMetadata.mutateAsync({
        scope,
        name,
        privacyLevel: privacyLevel === SENSITIVE_PRIVACY_LEVEL ? NOT_SENSITIVE_PRIVACY_LEVEL : SENSITIVE_PRIVACY_LEVEL,
      });
    } catch {
      snackbars.create({
        id: 'attribute.change-sensitivity.error',
        variant: 'critical',
        message: "An error occurred when changing this attribute's sensitivity. Try again.",
      });
    } finally {
      setIsChangingSensitivity(false);
    }
  };

  const confirmDelete = async () => {
    const confirmed = await confirm({
      title: `Delete attribute: ${name}`,
      message: (
        <DeleteAttributeMessage workspaceId={workspaceId} name={name} peoplePlural={taxonomy.term('people.plural')} />
      ),
      confirmLabel: 'Delete attribute',
      isCritical: true,
      // Typing the name is what makes this a decision rather than a click.
      confirmationPhrase: name,
    });
    if (!confirmed) {
      return;
    }

    try {
      await remove.mutateAsync({ scope, name });
    } catch {
      snackbars.create({
        id: 'data-index.attribute.delete.error',
        variant: 'critical',
        message: 'An error occurred while deleting your attribute. Try again later.',
      });
      return;
    }

    snackbars.create({
      id: 'data-index.attribute.delete.success',
      message: 'Your attribute has been scheduled for deletion.',
    });
    // Back to the attributes list, which is the only list this can return
    // to: the menu holding Delete renders for a person attribute alone, as
    // it does in the page this replaces.
    const listHref = router.buildLocation({ to: dataIndexListPath(String(workspaceId), 'attributes') }).href;
    navigate(listHref);
  };

  return { saveDetails, toggleSensitivity, isChangingSensitivity, confirmDelete };
}
